import { ViewIds } from '@ardoq/api-types';
import {
  type FeatureContext,
  featureOperations,
  Features,
} from '@ardoq/features';
import { isPresentationMode } from 'env';
import {
  MetaInfo,
  MetaInfoViewStatus,
} from '../streams/views/mainContent/types';
import { type I18nLocale, t } from '@ardoq/i18n';
import {
  type ViewDefinition,
  VIEWPOINT_MODE_VIEW_DEFINITIONS,
  viewIdToTranslationKey,
  viewIdToUserFriendlyName,
  WORKSPACE_MODE_VIEW_DEFINITIONS,
} from './metaInfoConsts';

export const viewHasLegend = (viewId: ViewIds): boolean =>
  WORKSPACE_MODE_VIEW_DEFINITIONS.some(
    ({ id, features }: ViewDefinition) =>
      id === viewId && Boolean(features?.hasLegend)
  ) ||
  VIEWPOINT_MODE_VIEW_DEFINITIONS.some(
    ({ id, features }: ViewDefinition) =>
      id === viewId && Boolean(features?.hasLegend)
  );

/**
 * Translated counterpart of `viewIdToUserFriendlyName`. Prefer this in
 * user-facing UI; falls back to the English label.
 */
export const getViewIdUserFriendlyName = (
  i18nLocale: I18nLocale,
  viewId: string | undefined
): string => {
  if (!viewId || !(viewId in viewIdToTranslationKey)) return '';
  const key = viewIdToTranslationKey[viewId as ViewIds];
  return t(i18nLocale, key, viewIdToUserFriendlyName[viewId as ViewIds]);
};

export const isViewEnabled = (
  { availabilityFeatures, ignoresFeaturesInPresentations }: ViewDefinition,
  featureContext: FeatureContext
): boolean =>
  availabilityFeatures.length === 0 ||
  availabilityFeatures.some(feature =>
    featureOperations.hasFeature(featureContext, feature)
  ) ||
  Boolean(ignoresFeaturesInPresentations && isPresentationMode());

export const getEnabledDefinitions = <Definition extends ViewDefinition>(
  definitions: readonly Definition[],
  featureContext: FeatureContext
): Definition[] =>
  definitions.filter(definition => isViewEnabled(definition, featureContext));

export const isHiddenByQuickStart = (
  isQuickStartView: boolean,
  featureContext: FeatureContext
): boolean =>
  !isQuickStartView &&
  featureOperations.hasFeature(
    featureContext,
    Features.QUICK_START_RESTRICT_AVAILABLE_VISUALIZATIONS
  );

export const toMetaInfo = (
  {
    availabilityFeatures: _availabilityFeatures,
    isQuickStartView: _isQuickStartView,
    discontinuedUnlessFeatures,
    ignoresFeaturesInPresentations,
    ...metaInfo
  }: ViewDefinition,
  featureContext: FeatureContext
): MetaInfo => {
  if (!discontinuedUnlessFeatures) {
    return metaInfo;
  }
  const isContinued =
    discontinuedUnlessFeatures.some(feature =>
      featureOperations.hasFeature(featureContext, feature)
    ) || Boolean(ignoresFeaturesInPresentations && isPresentationMode());
  return isContinued
    ? metaInfo
    : { ...metaInfo, viewStatus: MetaInfoViewStatus.DISCONTINUED };
};
